import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';
import { toast } from 'react-toastify';
import {
  TASK_TYPES,
  TASK_TYPE_LABELS,
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_PRIORITIES,
} from '@claimup/shared/constants/assignments';

const CreateAssignmentForm = ({ expedientId, onSubmit, onCancel, isSubmitting, resetTrigger, assignment = null }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const getInitialFormData = () => {
    if (assignment) {
      // Edit mode: populate with existing assignment data
      const assignmentDate = new Date(assignment.date);
      const year = assignmentDate.getFullYear();
      const month = String(assignmentDate.getMonth() + 1).padStart(2, '0');
      const day = String(assignmentDate.getDate()).padStart(2, '0');
      const hours = String(assignmentDate.getHours()).padStart(2, '0');
      const minutes = String(assignmentDate.getMinutes()).padStart(2, '0');
      const dateTimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;

      return {
        assigned_to: assignment.assigned_to?._id || assignment.assigned_to || '',
        description: assignment.description || '',
        status: assignment.status || 'pending',
        date: dateTimeLocal,
        priority: assignment.priority || 'medium',
        task_type: assignment.task_type || '',
        notes: assignment.notes || '',
      };
    }

    // Create mode: use current date/time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const dateTimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;

    return {
      assigned_to: '',
      description: '',
      status: 'pending',
      date: dateTimeLocal,
      priority: 'medium',
      task_type: '',
      notes: '',
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());

  // Fetch users when form is rendered (modal opens) / cambia el expediente.
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expedientId]);

  // Reset form when resetTrigger changes (after successful submission or modal opens)
  useEffect(() => {
    if (resetTrigger || assignment) {
      setFormData(getInitialFormData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger, assignment]);

  const fetchUsers = async () => {
    if (!expedientId) return;
    try {
      setLoadingUsers(true);
      // Asignables: admins + gestores asignados a este expediente.
      const response = await axios.get(
        `${baseAPI}/assignments/assignable-users?expedientId=${expedientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar la lista de usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validation
    if (!formData.description.trim()) {
      toast.error('La descripción es requerida');
      return;
    }
    if (!formData.date) {
      toast.error('La fecha es requerida');
      return;
    }
    if (!formData.task_type) {
      toast.error('Debe seleccionar un tipo de tarea');
      return;
    }
    if (!expedientId) {
      toast.error('ID de expediente no válido');
      return;
    }

    // Convert datetime-local to ISO string
    const dateISO = new Date(formData.date).toISOString();

    const assignmentData = {
      assigned_to: formData.assigned_to || undefined,
      expedient_id: expedientId,
      description: formData.description.trim(),
      status: formData.status,
      date: dateISO,
      priority: formData.priority,
      task_type: formData.task_type,
      notes: formData.notes.trim() || undefined,
    };

    if (onSubmit) {
      onSubmit(assignmentData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      {/* Assigned To */}
      <div>
        <label htmlFor='assigned_to' className='block text-sm font-medium text-gray-700 mb-1'>
          Asignado a
        </label>
        {loadingUsers ? (
          <p className='text-sm text-gray-500'>Cargando usuarios...</p>
        ) : (
          <select
            id='assigned_to'
            name='assigned_to'
            value={formData.assigned_to}
            onChange={handleInputChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
          >
            <option value=''>Sin asignar</option>
            <option value='AIAgent'>IA (automática)</option>
            {users.map(user => (
              <option key={user._id} value={user._id}>
                {user.username} {user.userlastname} ({user.usermail})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor='description' className='block text-sm font-medium text-gray-700 mb-1'>
          Descripción <span className='text-red-500'>*</span>
        </label>
        <textarea
          id='description'
          name='description'
          value={formData.description}
          onChange={handleInputChange}
          required
          rows={3}
          className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
          placeholder='Descripción del assignment'
        />
      </div>

      {/* Task Type */}
      <div>
        <label htmlFor='task_type' className='block text-sm font-medium text-gray-700 mb-1'>
          Tipo de tarea <span className='text-red-500'>*</span>
        </label>
        <select
          id='task_type'
          name='task_type'
          value={formData.task_type}
          onChange={handleInputChange}
          required
          className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
        >
          <option value=''>Seleccionar tipo</option>
          {TASK_TYPES.map(type => (
            <option key={type} value={type}>
              {TASK_TYPE_LABELS[type] || type}
            </option>
          ))}
        </select>
      </div>

      {/* Date and Time */}
      <div>
        <label htmlFor='date' className='block text-sm font-medium text-gray-700 mb-1'>
          Fecha y hora <span className='text-red-500'>*</span>
        </label>
        <input
          type='datetime-local'
          id='date'
          name='date'
          value={formData.date}
          onChange={handleInputChange}
          required
          disabled={!!assignment}
          readOnly={!!assignment}
          className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed'
        />
      </div>

      {/* Priority and Status */}
      <div className='grid grid-cols-2 gap-4'>
        {/* Priority */}
        <div>
          <label htmlFor='priority' className='block text-sm font-medium text-gray-700 mb-1'>
            Prioridad
          </label>
          <select
            id='priority'
            name='priority'
            value={formData.priority}
            onChange={handleInputChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
          >
            {ASSIGNMENT_PRIORITIES.map(priority => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <label htmlFor='status' className='block text-sm font-medium text-gray-700 mb-1'>
            Estado
          </label>
          <select
            id='status'
            name='status'
            value={formData.status}
            onChange={handleInputChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
          >
            {ASSIGNMENT_STATUSES.map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor='notes' className='block text-sm font-medium text-gray-700 mb-1'>
          Notas
        </label>
        <textarea
          id='notes'
          name='notes'
          value={formData.notes}
          onChange={handleInputChange}
          rows={3}
          className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
          placeholder='Notas adicionales (opcional)'
        />
      </div>

      {/* Form Actions */}
      <div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
        <button
          type='button'
          onClick={onCancel}
          disabled={isSubmitting}
          className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          Cancelar
        </button>
        <button
          type='submit'
          disabled={isSubmitting}
          className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isSubmitting
            ? assignment
              ? 'Actualizando...'
              : 'Creando...'
            : assignment
              ? 'Guardar gestión'
              : 'Crear gestión'}
        </button>
      </div>
    </form>
  );
};

export default CreateAssignmentForm;
